import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useIssueState} from '@github-ui/issue-pr-state'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ClosedByPullRequestsReferences} from '@github-ui/list-view-items-issues-prs/ClosedByPullRequestsReferences'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {testIdProps} from '@github-ui/test-id-props'
import {CircleIcon, PersonIcon} from '@primer/octicons-react'
import {AvatarStack, Button, Link} from '@primer/react'
import type {PropsWithChildren} from 'react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {getIssueSearchURL} from '../../../utils/helpers'
import type {
  PaginatedRelationshipsListAllViewItemFragment$data,
  PaginatedRelationshipsListAllViewItemFragment$key,
} from './__generated__/PaginatedRelationshipsListAllViewItemFragment.graphql'
import styles from './PaginatedRelationshipsListAllView.module.css'

export const TEST_ID_SKELETON_ITEM = '__test-paginated-relationships-list-item-skeleton'
export const TEST_ID_PULL_REQUEST_METADATA = '__test-relationships-list-all-view-pull-request-metadata'

export const PAGE_SIZE = 25
const SKELETON_ROWS = 3

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PaginatedRelationshipsListAllViewItemFragment = graphql`
  fragment PaginatedRelationshipsListAllViewItemFragment on Issue {
    id
    url
    number
    titleHTML
    state
    stateReason
    repository {
      name
      nameWithOwner
      owner {
        login
      }
    }
    assignees(first: 10) {
      totalCount
      edges {
        node {
          id
          login
          avatarUrl
        }
      }
    }
    closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
      totalCount
    }
  }
`

type RelationshipsListAllViewPaginatedProps = PropsWithChildren<{
  accessibleTitle: string
  isLoadingNext: boolean
  hasNextPage: boolean
  onLoadMore: () => void
}>
export function RelationshipsListAllViewPaginated({
  accessibleTitle,
  isLoadingNext,
  hasNextPage,
  onLoadMore,
  children,
}: RelationshipsListAllViewPaginatedProps) {
  const loadingSkeletonItems = []
  if (isLoadingNext) {
    for (let i = 0; i < SKELETON_ROWS; i += 1) {
      loadingSkeletonItems.push(<RelationshipListItemSkeleton key={i} />)
    }
  }

  return (
    <>
      <ListView title={accessibleTitle} variant="compact">
        {children}
        {isLoadingNext && loadingSkeletonItems}
      </ListView>
      {hasNextPage && (
        <div className={styles.PaginatedRelationshipsListAllView_ControlWrapper}>
          <Button onClick={onLoadMore} disabled={isLoadingNext}>
            {LABELS.loadMoreItems}
          </Button>
        </div>
      )}
    </>
  )
}

type RelationshipsListItemProps = {
  issueFragment: PaginatedRelationshipsListAllViewItemFragment$key
  sourceIssueRepositoryNameWithOwner?: string
}
export function RelationshipsListItem({issueFragment, sourceIssueRepositoryNameWithOwner}: RelationshipsListItemProps) {
  const issue = useFragment(PaginatedRelationshipsListAllViewItemFragment, issueFragment)

  const {sourceIcon} = useIssueState({
    state: issue.state,
    stateReason: issue.stateReason,
  })
  const IconComponent = sourceIcon('Issue')

  const canonicalIssueReference = getCanonicalIssueReference(
    sourceIssueRepositoryNameWithOwner,
    issue.repository.nameWithOwner,
    issue.number,
  )

  return (
    <ListItem
      title={<RelationshipsListItemTitle issue={issue} />}
      metadata={<RelationshipsListItemMetadata issue={issue} />}
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual icon={IconComponent} />
      </ListItemLeadingContent>

      <ListItemMainContent>
        <ListItemDescription>{canonicalIssueReference}</ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

export function RelationshipsListViewLoadingSkeleton() {
  const rows = []
  for (let i = 0; i < SKELETON_ROWS; i += 1) {
    rows.push(<RelationshipListItemSkeleton key={i} />)
  }
  return (
    <>
      <ListView title={LABELS.relationLoadingList} variant="compact">
        {rows}
      </ListView>
    </>
  )
}

type RelationshipsListItemTitleProps = {
  issue: {titleHTML: string; url: string}
}
function RelationshipsListItemTitle({issue}: RelationshipsListItemTitleProps) {
  return (
    <ListItemTitle
      value={<UnsafeHTMLText as="span" html={issue.titleHTML} />}
      href={issue.url}
      anchorClassName="markdown-title"
    />
  )
}

type RelationshipsListItemMetadataProps = {
  issue: PaginatedRelationshipsListAllViewItemFragment$data
}
function RelationshipsListItemMetadata({issue}: RelationshipsListItemMetadataProps) {
  return (
    <>
      <ListItemMetadata {...testIdProps(TEST_ID_PULL_REQUEST_METADATA)}>
        <ClosedByPullRequestsReferences issueId={issue.id} closedByPullRequestsReferencesKey={issue} />
      </ListItemMetadata>
      <ListItemMetadata>
        <RelationshipsListItemAvatarMetadata
          owner={issue.repository.owner.login}
          repo={issue.repository.name}
          assignees={issue.assignees}
        />
      </ListItemMetadata>
    </>
  )
}

type RelationshipsListItemAvatarMetadataProps = {
  owner: string
  repo: string
  assignees: PaginatedRelationshipsListAllViewItemFragment$data['assignees']
}
function RelationshipsListItemAvatarMetadata({owner, repo, assignees}: RelationshipsListItemAvatarMetadataProps) {
  const edges = assignees.edges

  if (assignees.totalCount === 0 || !edges) return <EmptyAvatar />

  const assigneeNodes = edges.map(edge => edge?.node).filter(node => !!node)

  return (
    <AvatarStack alignRight className={styles.PaginatedRelationshipsListAllView_AvatarArea}>
      {assigneeNodes.map(assignee => (
        <AssigneeAvatarLink key={assignee.id} owner={owner} repo={repo} assignee={assignee} />
      ))}
    </AvatarStack>
  )
}

type AssigneeAvatarLinkProps = {
  owner: string
  repo: string
  assignee: {id: string; login: string; avatarUrl: string}
}
function AssigneeAvatarLink({owner, repo, assignee}: AssigneeAvatarLinkProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('user', {login: assignee.login, fixedPositioning: true})

  return (
    <Link
      href={getIssueSearchURL({owner, repo}, 'assignee', assignee.login)}
      {...hovercardAttrs}
      className={styles.PaginatedRelationshipsListAllView_AvatarLink}
    >
      <GitHubAvatar alt={assignee.login} src={assignee.avatarUrl} />
    </Link>
  )
}

function EmptyAvatar() {
  return (
    <div className={styles.PaginatedRelationshipsListAllView_EmptyAvatar}>
      <PersonIcon size={16} />
      <CircleIcon size={24} />
    </div>
  )
}

function RelationshipListItemSkeleton() {
  return (
    <ListItem
      {...testIdProps(TEST_ID_SKELETON_ITEM)}
      title={
        <ListItemTitle value={''}>
          <LoadingSkeleton height="20px" width="30ch" />
        </ListItemTitle>
      }
      metadata={
        <>
          <ListItemMetadata>
            <LoadingSkeleton variant="rounded" width="4ch" />
          </ListItemMetadata>
          <ListItemMetadata>
            <LoadingSkeleton variant="elliptical" width="20px" height="20px" />
          </ListItemMetadata>
        </>
      }
    />
  )
}

const getCanonicalIssueReference = (
  sourceIssueRepositoryNameWithOwner: string = '',
  targetIssueRepositoryNameWithOwner: string,
  issueNumber: number,
) => {
  const isSameRepositoryAsOwnerIssue =
    !sourceIssueRepositoryNameWithOwner || sourceIssueRepositoryNameWithOwner === targetIssueRepositoryNameWithOwner
  return `${isSameRepositoryAsOwnerIssue ? '' : targetIssueRepositoryNameWithOwner}#${issueNumber}`
}
